import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUUpLeft, ClockCounterClockwise } from "@phosphor-icons/react";

import { useLang } from "../../contexts/LanguageContext";
import { API_BASE as API } from "../../lib/apiBase";

/**
 * Tâche 1390 (Eric 2026-06-30) — Journal des renommages de locuteurs.
 *
 * Après 1360, on sait ce que coûte un nom de locuteur perdu : il faut relancer
 * une diarisation payante. Chaque renommage laisse donc une trace nominative,
 * restaurable en un clic.
 *
 * Deux choix d'affichage qui ne vont pas de soi :
 *   - une entrée déjà restaurée reste VISIBLE mais son bouton disparaît : la
 *     retirer effacerait la trace de qui a corrigé quoi, et re-restaurer
 *     renommerait des segments qui ne lui appartiennent plus ;
 *   - le journal ne se charge qu'au dépli. Une vidéo peut en porter des
 *     dizaines, et personne ne les lit à chaque ouverture de page.
 */
export const SpeakerRenameLogPanel = ({ videoId, onRestored }) => {
  const { tr } = useLang();
  const [ouvert, setOuvert] = useState(false);
  const [entrees, setEntrees] = useState(null);
  const [enCours, setEnCours] = useState(null);

  const charger = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/videos/${videoId}/speakers/rename-log`,
        { withCredentials: true },
      );
      setEntrees(data.entries || []);
    } catch {
      setEntrees([]);
      toast.error(tr("Journal illisible", "Could not read the log"));
    }
  }, [videoId, tr]);

  useEffect(() => {
    if (ouvert && entrees === null) charger();
  }, [ouvert, entrees, charger]);

  const restaurer = async (entree) => {
    setEnCours(entree.id);
    try {
      const { data } = await axios.post(
        `${API}/api/videos/${videoId}/speakers/rename-log/${entree.id}/restore`,
        {},
        { withCredentials: true },
      );
      const n = data.segments_restaures ?? 0;
      toast.success(tr(`${n} segment(s) restaurés`, `${n} segment(s) restored`));
      if (data.segments_sans_instantane > 0) {
        toast.warning(
          tr(
            `${data.segments_sans_instantane} segment(s) portaient déjà ce nom avant : leur nom d'origine n'est pas connu, ils sont laissés tels quels.`,
            `${data.segments_sans_instantane} segment(s) already carried this name before: their original name is unknown, they are left untouched.`,
          ),
          { duration: 12000 },
        );
      }
      await charger();
      if (onRestored) await onRestored();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || tr("Restauration impossible", "Restore failed"),
      );
    }
    setEnCours(null);
  };

  return (
    <div className="mt-3 rounded-lg border border-zinc-200 bg-white">
      <button
        type="button"
        onClick={() => setOuvert((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
        data-testid="speaker-rename-log-toggle"
      >
        <ClockCounterClockwise size={14} />
        {tr("Journal des renommages de locuteurs", "Speaker rename log")}
        <span className="ml-auto text-zinc-400">{ouvert ? "−" : "+"}</span>
      </button>

      {ouvert && entrees === null && (
        <p className="px-3 pb-3 text-xs text-zinc-500" data-testid="speaker-rename-log-loading">
          {tr("Lecture…", "Reading…")}
        </p>
      )}

      {ouvert && entrees !== null && entrees.length === 0 && (
        <p className="px-3 pb-3 text-xs text-zinc-500" data-testid="speaker-rename-log-empty">
          {tr(
            "Aucun renommage de locuteur sur cette vidéo.",
            "No speaker rename on this video.",
          )}
        </p>
      )}

      {ouvert && entrees !== null && entrees.length > 0 && (
        <ul className="px-3 pb-3 space-y-2" data-testid="speaker-rename-log-list">
          {entrees.map((e) => {
            const depuis = (e.names || []).join(" · ");
            const quand = e.at ? new Date(e.at).toLocaleString() : "?";
            return (
              <li
                key={e.id}
                className="text-xs border-t border-zinc-100 pt-2 flex items-start gap-3"
                data-testid={`speaker-rename-log-entry-${e.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-zinc-800">
                    <span className="line-through text-zinc-500">{depuis}</span>
                    {" → "}
                    <strong>{e.canonical}</strong>
                    <span className="text-zinc-400">
                      {" "}
                      ({e.total_segments}{" "}
                      {e.action === "segment"
                        ? tr("tour", "turn")
                        : tr("segments", "segments")}
                      )
                    </span>
                  </p>
                  <p className="text-zinc-500 mt-0.5">
                    {tr("par", "by")} {e.by || tr("inconnu", "unknown")} · {quand}
                    {e.reverted && (
                      <span className="text-teal-700 font-medium">
                        {" · "}
                        {tr("restauré", "restored")}
                        {e.reverted_by ? ` ${tr("par", "by")} ${e.reverted_by}` : ""}
                      </span>
                    )}
                  </p>
                </div>
                {!e.reverted && (
                  <button
                    type="button"
                    onClick={() => restaurer(e)}
                    disabled={enCours === e.id}
                    className="shrink-0 flex items-center gap-1 px-2 py-1 rounded border border-zinc-300 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                    data-testid={`speaker-rename-log-restore-${e.id}`}
                  >
                    <ArrowUUpLeft size={12} />
                    {enCours === e.id
                      ? tr("Restauration…", "Restoring…")
                      : tr("Restaurer", "Restore")}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SpeakerRenameLogPanel;
